/**
 * @file tipApi.js
 * @description 微信打赏 API（预下单、查单）
 * @module features/comic/api/tipApi
 *
 * 接口文档：comic/doc/api/pay/
 * - amount list: POST /comic/v1/api/pay/tip/amount/list
 * - prepare: POST /comic/v1/api/pay/tip/wechat/prepare
 * - status:  POST /comic/v1/api/pay/tip/wechat/order/status
 * - byOutTradeNo: POST /comic/v1/api/pay/tip/wechat/order/byOutTradeNo
 * - notify: 微信服务端回调，前端不调用
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'
import { getAuthToken } from '@/shared/utils/auth'
import { ERR_WX_MP_OAUTH_REQUIRED, normalizeApiCode } from '@/shared/utils/apiErrorCodes'
import { handleWxMpOAuthRequiredResponse, getOAuthRedirectPage } from '@/shared/utils/wxMpOAuth'

/**
 * JSAPI 预下单缺 wx_mp_open_id（prepare code=10102）
 */
export class TipWxMpOAuthRequiredError extends Error {
  /**
   * @param {string} [oauthAuthorizeUrl] 后端拼好的整页 OAuth 入口
   * @param {string} [message]
   */
  constructor(oauthAuthorizeUrl = '', message) {
    super(message || '请先完成微信授权后再打赏')
    this.name = 'TipWxMpOAuthRequiredError'
    this.code = ERR_WX_MP_OAUTH_REQUIRED
    this.oauthAuthorizeUrl = typeof oauthAuthorizeUrl === 'string' ? oauthAuthorizeUrl : ''
  }
}

/**
 * @returns {Record<string, string>}
 */
function buildOAuthRedirectHeader() {
  const page = getOAuthRedirectPage()
  if (!page) return {}
  return { 'X-OAuth-Redirect': page }
}

/**
 * @returns {import('axios').AxiosRequestConfig}
 */
function tipAuthConfig() {
  return {
    headers: {
      'COMIC-WEB-TOKEN': getAuthToken(),
      'Content-Type': 'application/json',
      ...buildOAuthRedirectHeader(),
    },
  }
}

/**
 * @param {import('axios').AxiosResponse} response
 * @returns {never}
 */
function throwTipApiError(response) {
  const body = response?.data ?? {}
  throw Object.assign(
    new Error(body.msg ?? body.errmsg ?? '请求失败'),
    { code: body.code }
  )
}

/** 打赏业务类型 */
export const PAY_ORDER_TYPE_TIP = 'TIP'

/**
 * 打赏金额档位列表（无需登录）
 * @returns {Promise<import('./tipTypes').TipAmountOptionVo[]>}
 */
export async function fetchTipAmountListApi() {
  const { data } = await axios.post(API.PAY.TIP_AMOUNT_LIST, {})
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '获取打赏档位失败'), {
      code: data.code,
    })
  }
  const items = Array.isArray(data.items) ? data.items : []
  return items.map((item) => ({
    code: String(item.code ?? ''),
    name: String(item.name ?? ''),
    amountCent: Number(item.amountCent) || 0,
    sort: typeof item.sort === 'number' ? item.sort : 0,
  }))
}

/**
 * 微信打赏预下单
 * @param {import('./tipTypes').PrepareTipWechatPayload} payload
 * @returns {Promise<import('./tipTypes').PrepareTipWechatVo>}
 */
export async function prepareTipWechatApi(payload) {
  const { data } = await axios.post(API.PAY.TIP_WECHAT_PREPARE, payload, tipAuthConfig())
  if (normalizeApiCode(data?.code) === ERR_WX_MP_OAUTH_REQUIRED) {
    if (handleWxMpOAuthRequiredResponse(data)) {
      return new Promise(() => {})
    }
    const oauthAuthorizeUrl =
      data.data && typeof data.data.oauthAuthorizeUrl === 'string'
        ? data.data.oauthAuthorizeUrl
        : ''
    throw new TipWxMpOAuthRequiredError(oauthAuthorizeUrl, data.msg)
  }
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '创建打赏订单失败'), {
      code: data.code,
    })
  }
  return data.data
}

/**
 * 按订单 ID 查询打赏状态
 * @param {{ orderId: string }} payload
 * @returns {Promise<import('./tipTypes').TipOrderStatusVo>}
 */
export async function queryTipOrderStatusByIdApi(payload) {
  const { data } = await axios.post(API.PAY.TIP_WECHAT_ORDER_STATUS, payload, tipAuthConfig())
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '查询订单失败'), {
      code: data.code,
    })
  }
  return data.data
}

/**
 * 按商户单号查询打赏状态
 * @param {{ outTradeNo: string }} payload
 * @returns {Promise<import('./tipTypes').TipOrderStatusVo>}
 */
export async function queryTipOrderStatusByOutTradeNoApi(payload) {
  const { data } = await axios.post(
    API.PAY.TIP_WECHAT_ORDER_BY_OUT_TRADE_NO,
    payload,
    tipAuthConfig()
  )
  if (data.code !== 200 && data.code !== 0) {
    throw Object.assign(new Error(data.msg ?? data.errmsg ?? '查询订单失败'), {
      code: data.code,
    })
  }
  return data.data
}
